import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker, Input, Message, toaster } from 'rsuite';
import { Button as NsiButton, Modal, sortRows, useLastPresent, useTableSort } from '@nsi-advisory-services/components';

import {
  useCreateTaskOrderInvoice,
  useDeleteTaskOrderInvoice,
  useProjects,
  useTaskOrders,
  useTasks,
  useTaskOrderInvoices,
  useUpdateTaskOrderInvoice,
} from '../hooks/useQueries';
import usePermissions from '../hooks/usePermissions';
import './ConfigurationSheet.css';
import './TaskOrderInvoicesPage.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return currencyFormatter.format(n);
};

const normalizeCurrencyInput = (value) => {
  const raw = String(value || '').replace(/[^\d.]/g, '');
  if (!raw) return '';

  const dotIndex = raw.indexOf('.');
  const hasDot = dotIndex >= 0;
  const integerPartRaw = hasDot ? raw.slice(0, dotIndex) : raw;
  const decimalPartRaw = hasDot ? raw.slice(dotIndex + 1).replace(/\./g, '') : '';

  const integerDigits = integerPartRaw.replace(/^0+(?=\d)/, '') || '0';
  const integerFormatted = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decimalDigits = decimalPartRaw.slice(0, 2);

  if (hasDot) {
    return `$${integerFormatted}.${decimalDigits}`;
  }
  return `$${integerFormatted}`;
};

const parseCurrencyInput = (value) => {
  const raw = String(value || '').replace(/[^\d.]/g, '');
  if (!raw) return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
};

const getTodayIsoDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isoToDate = (iso) => {
  const value = String(iso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const dt = new Date(`${value}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const dateToIso = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export default function TaskOrderInvoicesPage() {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = Number(params?.projectId);
  const canManageInvoices = usePermissions().canProject(projectId, 'finance');
  const taskOrderId = Number(params?.taskOrderId);

  const {
    data: projects = [],
  } = useProjects();

  const {
    data: taskOrdersData,
  } = useTaskOrders(projectId, {
    enabled: Number.isInteger(projectId),
  });

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    error: invoicesError,
  } = useTaskOrderInvoices(projectId, taskOrderId, {
    enabled: Number.isInteger(projectId) && Number.isInteger(taskOrderId),
  });

  const {
    data: taskRows,
    isLoading: tasksLoading,
  } = useTasks({ taskOrderId }, {
    enabled: Number.isInteger(taskOrderId),
  });

  const createTaskOrderInvoice = useCreateTaskOrderInvoice();
  const deleteInvoice = useDeleteTaskOrderInvoice();
  const updateInvoice = useUpdateTaskOrderInvoice();

  const invoiceRows = useMemo(() => {
    const rows = Array.isArray(invoicesData) ? [...invoicesData] : [];
    rows.sort((a, b) => {
      const aDate = String(a?.invoiceDate || '');
      const bDate = String(b?.invoiceDate || '');
      if (aDate !== bDate) return bDate.localeCompare(aDate);
      return Number(b?.id ?? 0) - Number(a?.id ?? 0);
    });
    return rows;
  }, [invoicesData]);

  const invoiceSort = useTableSort('invoiceDate', 'desc');

  const invoiceTotal = useMemo(
    () => invoiceRows.reduce((sum, row) => sum + Number(row?.invoiceLaborAmount ?? 0) + Number(row?.invoiceOdcAmount ?? 0), 0),
    [invoiceRows]
  );

  const [newInvoiceDate, setNewInvoiceDate] = useState(() => getTodayIsoDate());
  const [newInvoiceLaborAmount, setNewInvoiceLaborAmount] = useState('');
  const [newInvoiceOdcAmount, setNewInvoiceOdcAmount] = useState('');
  const [newInvoiceOdcByActivity, setNewInvoiceOdcByActivity] = useState({});
  const [newInvoiceComment, setNewInvoiceComment] = useState('');
  const [newInvoiceFileLink, setNewInvoiceFileLink] = useState('');
  const [addInvoiceModalOpen, setAddInvoiceModalOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const invoiceToDeleteShown = useLastPresent(invoiceToDelete); // keeps the dialog steady while it fades out
  const [invoiceToEdit, setInvoiceToEdit] = useState(null);
  const invoiceToEditShown = useLastPresent(invoiceToEdit); // keeps the dialog steady while it fades out
  const [editInvoiceDate, setEditInvoiceDate] = useState('');
  const [editInvoiceLaborAmount, setEditInvoiceLaborAmount] = useState('');
  const [editInvoiceOdcAmount, setEditInvoiceOdcAmount] = useState('');
  const [editInvoiceOdcByActivity, setEditInvoiceOdcByActivity] = useState({});
  const [editInvoiceComment, setEditInvoiceComment] = useState('');
  const [editInvoiceFileLink, setEditInvoiceFileLink] = useState('');

  const odcActivities = useMemo(() => {
    const source = Array.isArray(taskRows) ? taskRows : [];
    const activityRows = [];
    for (const task of source) {
      const taskType = String(task?.taskType || '').trim().toLowerCase();
      if (taskType !== 'odc') continue;

      const taskName = String(task?.task || '').trim() || 'ODC Task';
      const activities = Array.isArray(task?.activities) ? task.activities : [];
      for (const activity of activities) {
        const activityId = Number(activity?.id);
        if (!Number.isInteger(activityId)) continue;
        const activityName = String(activity?.name || '').trim() || `Activity ${activityId}`;
        activityRows.push({
          activityId,
          label: `${activityName} (${taskName})`,
        });
      }
    }

    activityRows.sort((a, b) => a.label.localeCompare(b.label));
    return activityRows;
  }, [taskRows]);

  const usesActivityOdcInput = odcActivities.length > 0;

  const projectName = useMemo(() => {
    const p = (Array.isArray(projects) ? projects : []).find((row) => Number(row?.id) === projectId);
    return String(p?.name || 'Project');
  }, [projects, projectId]);

  const taskOrderBudget = useMemo(() => {
    const rows = Array.isArray(taskOrdersData) ? taskOrdersData : [];
    const hit = rows.find((row) => Number(row?.id) === taskOrderId);
    const raw = hit?.budgetTarget ?? hit?.budget_target;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [taskOrdersData, taskOrderId]);

  const taskOrderName = useMemo(() => {
    const rows = Array.isArray(taskOrdersData) ? taskOrdersData : [];
    const hit = rows.find((row) => Number(row?.id) === taskOrderId);
    return String(hit?.name || `Task Order ${taskOrderId}`);
  }, [taskOrdersData, taskOrderId]);

  const parseOdcActivityInputState = useCallback((inputState) => {
    let total = 0;
    const payload = [];

    for (const activity of odcActivities) {
      const raw = String(inputState?.[activity.activityId] || '').trim();
      if (!raw) continue;

      const amount = parseCurrencyInput(raw);
      if (!Number.isFinite(amount) || amount < 0) {
        return {
          error: `ODC amount for ${activity.label} must be a number greater than or equal to 0.`,
        };
      }

      total += amount;
      if (amount > 0) {
        payload.push({ activityId: activity.activityId, amount });
      }
    }

    return { payload, total };
  }, [odcActivities]);

  const buildActivityInputStateFromInvoice = (invoice) => {
    const next = {};
    for (const activity of odcActivities) {
      next[activity.activityId] = '';
    }

    const existing = Array.isArray(invoice?.invoiceOdcActivityAmounts) ? invoice.invoiceOdcActivityAmounts : [];
    let hasExistingBreakdown = false;
    for (const item of existing) {
      const activityId = Number(item?.activityId);
      const amount = Number(item?.amount);
      if (!Number.isInteger(activityId) || !Number.isFinite(amount) || amount < 0) continue;
      if (!(activityId in next)) continue;
      next[activityId] = normalizeCurrencyInput(String(amount));
      hasExistingBreakdown = true;
    }

    if (!hasExistingBreakdown && odcActivities.length === 1) {
      const fallbackTotal = Number(invoice?.invoiceOdcAmount ?? 0);
      if (Number.isFinite(fallbackTotal) && fallbackTotal > 0) {
        next[odcActivities[0].activityId] = normalizeCurrencyInput(String(fallbackTotal));
      }
    }

    return next;
  };

  const newInvoiceRunningTotal = useMemo(() => {
    const labor = parseCurrencyInput(newInvoiceLaborAmount);
    const odc = usesActivityOdcInput
      ? parseOdcActivityInputState(newInvoiceOdcByActivity).total
      : parseCurrencyInput(newInvoiceOdcAmount);
    const laborSafe = Number.isFinite(labor) && labor >= 0 ? labor : 0;
    const odcSafe = Number.isFinite(odc) && odc >= 0 ? odc : 0;
    return laborSafe + odcSafe;
  }, [newInvoiceLaborAmount, newInvoiceOdcAmount, newInvoiceOdcByActivity, usesActivityOdcInput, parseOdcActivityInputState]);

  const editInvoiceRunningTotal = useMemo(() => {
    const labor = parseCurrencyInput(editInvoiceLaborAmount);
    const odc = usesActivityOdcInput
      ? parseOdcActivityInputState(editInvoiceOdcByActivity).total
      : parseCurrencyInput(editInvoiceOdcAmount);
    const laborSafe = Number.isFinite(labor) && labor >= 0 ? labor : 0;
    const odcSafe = Number.isFinite(odc) && odc >= 0 ? odc : 0;
    return laborSafe + odcSafe;
  }, [editInvoiceLaborAmount, editInvoiceOdcAmount, editInvoiceOdcByActivity, usesActivityOdcInput, parseOdcActivityInputState]);

  const openAddInvoiceModal = () => {
    const nextByActivity = {};
    for (const activity of odcActivities) {
      nextByActivity[activity.activityId] = '';
    }
    setNewInvoiceOdcByActivity(nextByActivity);
    setAddInvoiceModalOpen(true);
  };

  const closeAddInvoiceModal = () => {
    if (createTaskOrderInvoice.isPending) return;
    setAddInvoiceModalOpen(false);
  };

  const submitAddInvoice = async () => {
    const invoiceDate = String(newInvoiceDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(invoiceDate)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Invoice date is required.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const laborAmount = parseCurrencyInput(newInvoiceLaborAmount);
    if (!Number.isFinite(laborAmount) || laborAmount < 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Labor amount must be a number greater than or equal to 0.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    let odcAmount = 0;
    let odcActivityPayload = null;
    if (usesActivityOdcInput) {
      const parsedOdcActivity = parseOdcActivityInputState(newInvoiceOdcByActivity);
      if (parsedOdcActivity.error) {
        toaster.push(
          <Message showIcon type="warning" closable>
            {parsedOdcActivity.error}
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
      odcAmount = Number(parsedOdcActivity.total ?? 0);
      odcActivityPayload = parsedOdcActivity.payload;
    } else {
      odcAmount = parseCurrencyInput(newInvoiceOdcAmount);
      if (!Number.isFinite(odcAmount) || odcAmount < 0) {
        toaster.push(
          <Message showIcon type="warning" closable>
            ODC amount must be a number greater than or equal to 0.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
    }

    const trimmedLink = String(newInvoiceFileLink || '').trim();
    if (trimmedLink) {
      try {
        const parsed = new URL(trimmedLink);
        if (!['http:', 'https:'].includes(String(parsed.protocol || '').toLowerCase())) {
          throw new Error('invalid protocol');
        }
      } catch {
        toaster.push(
          <Message showIcon type="warning" closable>
            File link must be a valid http(s) URL.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
    }

    try {
      await createTaskOrderInvoice.mutateAsync({
        projectId,
        taskOrderId,
        payload: {
          invoiceDate,
          invoiceLaborAmount: laborAmount,
          invoiceOdcAmount: odcAmount,
          invoiceOdcActivityAmounts: odcActivityPayload,
          invoiceComment: String(newInvoiceComment || '').trim() || null,
          invoiceFileLink: trimmedLink || null,
        },
      });

      toaster.push(
        <Message showIcon type="success" closable>
          Invoice saved.
        </Message>,
        { placement: 'topEnd' }
      );

      setNewInvoiceLaborAmount('');
      setNewInvoiceOdcAmount('');
      setNewInvoiceOdcByActivity({});
      setNewInvoiceComment('');
      setNewInvoiceFileLink('');
      setNewInvoiceDate(getTodayIsoDate());
      setAddInvoiceModalOpen(false);
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Failed to save invoice.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const openDeleteInvoiceModal = (invoice) => {
    const invoiceId = Number(invoice?.id);
    if (!Number.isInteger(invoiceId)) return;

    setInvoiceToDelete({
      id: invoiceId,
      invoiceDate: String(invoice?.invoiceDate || ''),
      invoiceAmount: Number(invoice?.invoiceLaborAmount ?? 0) + Number(invoice?.invoiceOdcAmount ?? 0),
    });
  };

  const closeDeleteInvoiceModal = () => {
    if (deleteInvoice.isPending) return;
    setInvoiceToDelete(null);
  };

  const submitDeleteInvoice = async () => {
    const invoiceId = Number(invoiceToDelete?.id);
    if (!Number.isInteger(invoiceId)) return;

    try {
      await deleteInvoice.mutateAsync({ projectId, taskOrderId, invoiceId });
      setInvoiceToDelete(null);
      toaster.push(
        <Message showIcon type="success" closable>
          Invoice deleted.
        </Message>,
        { placement: 'topEnd' }
      );
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Failed to delete invoice.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const openEditInvoiceModal = (invoice) => {
    const invoiceId = Number(invoice?.id);
    if (!Number.isInteger(invoiceId)) return;

    setInvoiceToEdit({
      id: invoiceId,
      invoiceDate: String(invoice?.invoiceDate || ''),
      invoiceAmount: Number(invoice?.invoiceAmount ?? 0),
    });
    setEditInvoiceDate(String(invoice?.invoiceDate || '').slice(0, 10));
    setEditInvoiceLaborAmount(
      Number.isFinite(Number(invoice?.invoiceLaborAmount))
        ? normalizeCurrencyInput(String(invoice?.invoiceLaborAmount ?? 0))
        : ''
    );
    setEditInvoiceOdcAmount(
      Number.isFinite(Number(invoice?.invoiceOdcAmount))
        ? normalizeCurrencyInput(String(invoice?.invoiceOdcAmount ?? 0))
        : ''
    );
    setEditInvoiceOdcByActivity(buildActivityInputStateFromInvoice(invoice));
    setEditInvoiceComment(String(invoice?.invoiceComment || ''));
    setEditInvoiceFileLink(String(invoice?.invoiceFileLink || ''));
  };

  const closeEditInvoiceModal = () => {
    if (updateInvoice.isPending) return;
    setInvoiceToEdit(null);
  };

  const submitEditInvoice = async () => {
    const invoiceId = Number(invoiceToEdit?.id);
    if (!Number.isInteger(invoiceId)) return;

    const invoiceDate = String(editInvoiceDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(invoiceDate)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Invoice date is required.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const laborAmount = parseCurrencyInput(editInvoiceLaborAmount);
    if (!Number.isFinite(laborAmount) || laborAmount < 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Labor amount must be a number greater than or equal to 0.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    let odcAmount = 0;
    let odcActivityPayload = null;
    if (usesActivityOdcInput) {
      const parsedOdcActivity = parseOdcActivityInputState(editInvoiceOdcByActivity);
      if (parsedOdcActivity.error) {
        toaster.push(
          <Message showIcon type="warning" closable>
            {parsedOdcActivity.error}
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
      odcAmount = Number(parsedOdcActivity.total ?? 0);
      odcActivityPayload = parsedOdcActivity.payload;
    } else {
      odcAmount = parseCurrencyInput(editInvoiceOdcAmount);
      if (!Number.isFinite(odcAmount) || odcAmount < 0) {
        toaster.push(
          <Message showIcon type="warning" closable>
            ODC amount must be a number greater than or equal to 0.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
    }

    const trimmedLink = String(editInvoiceFileLink || '').trim();
    if (trimmedLink) {
      try {
        const parsed = new URL(trimmedLink);
        if (!['http:', 'https:'].includes(String(parsed.protocol || '').toLowerCase())) {
          throw new Error('invalid protocol');
        }
      } catch {
        toaster.push(
          <Message showIcon type="warning" closable>
            File link must be a valid http(s) URL.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
    }

    try {
      await updateInvoice.mutateAsync({
        projectId,
        taskOrderId,
        invoiceId,
        payload: {
          invoiceDate,
          invoiceLaborAmount: laborAmount,
          invoiceOdcAmount: odcAmount,
          invoiceOdcActivityAmounts: odcActivityPayload,
          invoiceComment: String(editInvoiceComment || '').trim() || null,
          invoiceFileLink: trimmedLink || null,
        },
      });

      setInvoiceToEdit(null);

      toaster.push(
        <Message showIcon type="success" closable>
          Invoice updated.
        </Message>,
        { placement: 'topEnd' }
      );
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Failed to update invoice.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)) {
    return (
      <div className="ppc-page">
        <Message type="error" showIcon>
          Invalid project/task order.
        </Message>
      </div>
    );
  }

  // Capped at the full track so an overrun paints it solid rather than
  // overflowing; the figure above says by how much.
  const invoicedPercent = taskOrderBudget ? (invoiceTotal / taskOrderBudget) * 100 : 0;
  const invoicedBarPercent = Math.min(100, Math.max(0, invoicedPercent));
  const invoicedIsOver = invoicedPercent > 100;

  const sortedInvoiceRows = sortRows(invoiceRows, invoiceSort.sortKey, invoiceSort.sortDir, {
    invoiceDate: (r) => String(r?.invoiceDate ?? ''),
    labor: (r) => Number(r?.invoiceLaborAmount ?? 0),
    odc: (r) => Number(r?.invoiceOdcAmount ?? 0),
    total: (r) => Number(r?.invoiceLaborAmount ?? 0) + Number(r?.invoiceOdcAmount ?? 0),
    comment: (r) => String(r?.invoiceComment ?? ''),
  });

  return (
    <div className="ppc-page ppc-toi">
      <header className="ppc-toi__header">
        <div style={{ minWidth: 0 }}>
          <div className="ppc-toi__crumbs">
            <button
              type="button"
              className="ppc-toi__crumb-link"
              onClick={() => navigate('/')}
            >
              Projects
            </button>
            <i className="bi bi-chevron-right ppc-toi__crumb-sep" aria-hidden="true" />
            <button
              type="button"
              className="ppc-toi__crumb-link"
              onClick={() => navigate(`/projects/${projectId}`)}
            >
              {projectName}
            </button>
            <i className="bi bi-chevron-right ppc-toi__crumb-sep" aria-hidden="true" />
            <button
              type="button"
              className="ppc-toi__crumb-link"
              onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}`)}
            >
              {taskOrderName}
            </button>
            <i className="bi bi-chevron-right ppc-toi__crumb-sep" aria-hidden="true" />
            <span className="ppc-toi__crumb-current">Invoices</span>
          </div>

          <h1 className="ppc-toi__title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <i className="bi bi-journal-text" aria-hidden="true" />
              Task Order Invoices
            </span>
          </h1>
        </div>

        <div className="ppc-toi__actions">
          <button
            type="button"
            className="ppc-toi-btn"
            onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}`)}
          >
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back to task order
          </button>

          <button
            type="button"
            className="ppc-toi-btn"
            onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}/actuals`)}
          >
            Actuals
            <i className="bi bi-clipboard-data" aria-hidden="true" />
          </button>
        </div>
      </header>

      <section className="ppc-toi-section">
        <div className="ppc-toi-section__head">
          <div className="ppc-toi-section__heading">
            <h2 className="ppc-toi-section__title">Invoices</h2>
            {invoiceRows.length > 0 ? (
              <span className="ppc-toi-count">{invoiceRows.length}</span>
            ) : null}
          </div>

          {canManageInvoices ? (
            <button
              type="button"
              className="ppc-toi-btn ppc-toi-btn--primary"
              onClick={openAddInvoiceModal}
              disabled={createTaskOrderInvoice.isPending}
            >
              Add Invoice
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          {invoicesError ? (
            <Message type="error" showIcon>
              Failed to load invoices.
            </Message>
          ) : null}

          {invoicesLoading ? <div className="ppc-toi-section__note">Loading invoices…</div> : null}

          {!invoicesLoading && !invoicesError && invoiceRows.length === 0 ? (
            <Message type="info" showIcon>
              No invoices yet for this task order.
            </Message>
          ) : null}

          {!invoicesLoading && !invoicesError && invoiceRows.length > 0 ? (
            <div className="ppc-cfg-tablescope ppc-toi-card">
              <div className="nsi-table-wrap">
                <table className="nsi-table">
                  <thead>
                    <tr>
                      <th scope="col" aria-sort={invoiceSort.ariaSort('invoiceDate')}>
                        <button type="button" className="nsi-table-sort" onClick={() => invoiceSort.toggleSort('invoiceDate')}>
                          Date
                          <i className={invoiceSort.sortIconClass('invoiceDate')} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" aria-sort={invoiceSort.ariaSort('labor')}>
                        <button type="button" className="nsi-table-sort" onClick={() => invoiceSort.toggleSort('labor')}>
                          Labor
                          <i className={invoiceSort.sortIconClass('labor')} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" aria-sort={invoiceSort.ariaSort('odc')}>
                        <button type="button" className="nsi-table-sort" onClick={() => invoiceSort.toggleSort('odc')}>
                          ODCs
                          <i className={invoiceSort.sortIconClass('odc')} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" aria-sort={invoiceSort.ariaSort('total')}>
                        <button type="button" className="nsi-table-sort" onClick={() => invoiceSort.toggleSort('total')}>
                          Total
                          <i className={invoiceSort.sortIconClass('total')} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" aria-sort={invoiceSort.ariaSort('comment')}>
                        <button type="button" className="nsi-table-sort" onClick={() => invoiceSort.toggleSort('comment')}>
                          Comment
                          <i className={invoiceSort.sortIconClass('comment')} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" style={{ width: 110 }}>File</th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedInvoiceRows.map((row) => {
                      const invoiceId = Number(row?.id);
                      const rowTotal = Number(row?.invoiceLaborAmount ?? 0) + Number(row?.invoiceOdcAmount ?? 0);

                      return (
                        <tr
                          key={invoiceId}
                          className="nsi-table__row"
                          onClick={() => canManageInvoices && openEditInvoiceModal(row)}
                          onKeyDown={(e) => {
                            if (canManageInvoices && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              openEditInvoiceModal(row);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          title="Edit invoice"
                        >
                          <td className="nsi-table__num">{String(row?.invoiceDate || '—')}</td>
                          <td className="nsi-table__num">
                            {formatCurrency(Number(row?.invoiceLaborAmount ?? 0))}
                          </td>
                          <td className="nsi-table__num">
                            {formatCurrency(Number(row?.invoiceOdcAmount ?? 0))}
                          </td>
                          <td className="nsi-table__num">
                            {formatCurrency(rowTotal)}
                          </td>
                          <td className="nsi-table__muted">{String(row?.invoiceComment || '—')}</td>
                          {/* The link is its own target, so it must not also open the row. */}
                          <td onClick={(e) => e.stopPropagation()}>
                            {row?.invoiceFileLink ? (
                              <a href={row.invoiceFileLink} target="_blank" rel="noreferrer">
                                Open Link
                              </a>
                            ) : (
                              <span className="nsi-table__muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    <tr>
                      <td>Total invoiced</td>
                      <td className="nsi-table__num">
                        {formatCurrency(invoiceRows.reduce((sum, row) => sum + Number(row?.invoiceLaborAmount ?? 0), 0))}
                      </td>
                      <td className="nsi-table__num">
                        {formatCurrency(invoiceRows.reduce((sum, row) => sum + Number(row?.invoiceOdcAmount ?? 0), 0))}
                      </td>
                      <td className="nsi-table__num">{formatCurrency(invoiceTotal)}</td>
                      <td />
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {taskOrderBudget !== null ? (
                <div className="ppc-toi-progress">
                  <div className="ppc-toi-progress__row">
                    <p className="ppc-toi-progress__label">Invoiced vs allocated budget</p>
                    <p className={`ppc-toi-progress__stat${invoicedIsOver ? ' ppc-toi-progress__stat--over' : ''}`}>
                      {invoicedIsOver
                        ? `${(invoicedPercent - 100).toFixed(2)}% over`
                        : `${(100 - invoicedPercent).toFixed(2)}% remaining`}
                    </p>
                  </div>

                  <div
                    className="ppc-toi-progress__track"
                    role="progressbar"
                    aria-valuenow={Math.round(invoicedPercent)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Invoiced as a share of the allocated budget"
                  >
                    <div
                      className={`ppc-toi-progress__fill${invoicedIsOver ? ' ppc-toi-progress__fill--over' : ''}`}
                      style={{ width: `${invoicedBarPercent}%` }}
                    />
                  </div>

                  <div className="ppc-toi-progress__scale">
                    <span>{`${formatCurrency(invoiceTotal)} invoiced`}</span>
                    <span>{`${formatCurrency(taskOrderBudget)} allocated`}</span>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      <Modal open={Boolean(invoiceToDelete)} onClose={closeDeleteInvoiceModal} size="xs">
        <Modal.Header>
          <Modal.Title>Delete invoice?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message showIcon type="warning" style={{ marginBottom: 12 }}>
            This cannot be undone.
          </Message>
          <div>
            {invoiceToDeleteShown
              ? `Invoice ${invoiceToDeleteShown.invoiceDate || '—'} • ${formatCurrency(invoiceToDeleteShown.invoiceAmount)}`
              : ''}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeDeleteInvoiceModal} disabled={deleteInvoice.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            onClick={submitDeleteInvoice}
            loading={deleteInvoice.isPending}
            disabled={deleteInvoice.isPending}
          >
            Delete Invoice
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={addInvoiceModalOpen} onClose={closeAddInvoiceModal} size="md">
        <Modal.Header>
          <Modal.Title>Add invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: usesActivityOdcInput ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Invoice date</div>
                <DatePicker
                  oneTap
                  cleanable={false}
                  format="MM/dd/yyyy"
                  value={isoToDate(newInvoiceDate)}
                  onChange={(value) => setNewInvoiceDate(dateToIso(value) || '')}
                  style={{ width: '100%' }}
                  disabled={createTaskOrderInvoice.isPending}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Labor amount</div>
                <Input
                  type="text"
                  value={newInvoiceLaborAmount}
                  onChange={(v) => setNewInvoiceLaborAmount(normalizeCurrencyInput(v))}
                  placeholder="$0.00"
                  disabled={createTaskOrderInvoice.isPending}
                />
              </div>

              {!usesActivityOdcInput ? (
                <div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>ODC amount</div>
                  <Input
                    type="text"
                    value={newInvoiceOdcAmount}
                    onChange={(v) => setNewInvoiceOdcAmount(normalizeCurrencyInput(v))}
                    placeholder="$0.00"
                    disabled={createTaskOrderInvoice.isPending}
                  />
                </div>
              ) : null}
            </div>

            {usesActivityOdcInput ? (
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontSize: 12, color: '#666' }}>ODC amounts by activity</div>
                {tasksLoading ? <div style={{ color: '#666' }}>Loading ODC activities...</div> : null}
                {odcActivities.map((activity) => (
                  <div
                    key={`new-odc-activity-${activity.activityId}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(220px, 1fr) minmax(140px, 200px)',
                      gap: 10,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ fontSize: 13 }}>{activity.label}</div>
                    <Input
                      type="text"
                      value={String(newInvoiceOdcByActivity[activity.activityId] || '')}
                      onChange={(v) =>
                        setNewInvoiceOdcByActivity((prev) => ({
                          ...prev,
                          [activity.activityId]: normalizeCurrencyInput(v),
                        }))
                      }
                      placeholder="$0.00"
                      disabled={createTaskOrderInvoice.isPending}
                    />
                  </div>
                ))}
              </div>
            ) : null}

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Comment (optional)</div>
              <Input
                as="textarea"
                rows={3}
                value={newInvoiceComment}
                onChange={(v) => setNewInvoiceComment(String(v || ''))}
                placeholder="Add context for this invoice..."
                disabled={createTaskOrderInvoice.isPending}
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>File link (optional, e.g. SharePoint)</div>
                <Input
                  type="text"
                  value={newInvoiceFileLink}
                  onChange={(v) => setNewInvoiceFileLink(String(v || ''))}
                  placeholder="https://..."
                  disabled={createTaskOrderInvoice.isPending}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 6 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: '#666' }}>Invoice total</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{formatCurrency(newInvoiceRunningTotal)}</div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={closeAddInvoiceModal}
            disabled={createTaskOrderInvoice.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={submitAddInvoice}
            loading={createTaskOrderInvoice.isPending}
            disabled={createTaskOrderInvoice.isPending}
          >
            Add Invoice
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={Boolean(invoiceToEdit)} onClose={closeEditInvoiceModal} size="md">
        <Modal.Header>
          <Modal.Title>Edit invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: usesActivityOdcInput ? 'repeat(2, minmax(140px, 1fr))' : 'repeat(3, minmax(140px, 1fr))',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Invoice date</div>
                <DatePicker
                  oneTap
                  cleanable={false}
                  format="MM/dd/yyyy"
                  value={isoToDate(editInvoiceDate)}
                  onChange={(value) => setEditInvoiceDate(dateToIso(value) || '')}
                  style={{ width: '100%' }}
                  disabled={updateInvoice.isPending}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Labor amount</div>
                <Input
                  type="text"
                  value={editInvoiceLaborAmount}
                  onChange={(v) => setEditInvoiceLaborAmount(normalizeCurrencyInput(v))}
                  placeholder="$0.00"
                  disabled={updateInvoice.isPending}
                />
              </div>
              {!usesActivityOdcInput ? (
                <div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>ODC amount</div>
                  <Input
                    type="text"
                    value={editInvoiceOdcAmount}
                    onChange={(v) => setEditInvoiceOdcAmount(normalizeCurrencyInput(v))}
                    placeholder="$0.00"
                    disabled={updateInvoice.isPending}
                  />
                </div>
              ) : null}
            </div>

            {usesActivityOdcInput ? (
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontSize: 12, color: '#666' }}>ODC amounts by activity</div>
                {tasksLoading ? <div style={{ color: '#666' }}>Loading ODC activities...</div> : null}
                {odcActivities.map((activity) => (
                  <div
                    key={`edit-odc-activity-${activity.activityId}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(220px, 1fr) minmax(140px, 200px)',
                      gap: 10,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ fontSize: 13 }}>{activity.label}</div>
                    <Input
                      type="text"
                      value={String(editInvoiceOdcByActivity[activity.activityId] || '')}
                      onChange={(v) =>
                        setEditInvoiceOdcByActivity((prev) => ({
                          ...prev,
                          [activity.activityId]: normalizeCurrencyInput(v),
                        }))
                      }
                      placeholder="$0.00"
                      disabled={updateInvoice.isPending}
                    />
                  </div>
                ))}
              </div>
            ) : null}

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Comment (optional)</div>
              <Input
                as="textarea"
                rows={3}
                value={editInvoiceComment}
                onChange={(v) => setEditInvoiceComment(String(v || ''))}
                placeholder="Add context for this invoice..."
                disabled={updateInvoice.isPending}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>File link (optional)</div>
              <Input
                type="text"
                value={editInvoiceFileLink}
                onChange={(v) => setEditInvoiceFileLink(String(v || ''))}
                placeholder="https://..."
                disabled={updateInvoice.isPending}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: '#666' }}>Invoice total</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{formatCurrency(editInvoiceRunningTotal)}</div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          left={
            <NsiButton
              variant="danger"
              disabled={updateInvoice.isPending || deleteInvoice.isPending}
              onClick={() => {
                const target = invoiceToEditShown;
                closeEditInvoiceModal();
                openDeleteInvoiceModal(target);
              }}
            >
              Delete
            </NsiButton>
          }
        >
          <NsiButton
            onClick={closeEditInvoiceModal}
            disabled={updateInvoice.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={submitEditInvoice}
            loading={updateInvoice.isPending}
            disabled={updateInvoice.isPending}
          >
            Save Changes
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
